import { ImageVideoPreview } from "../../../common/ImageVideoPreview";
import { AnimationPreviewWrapper } from "./AnimationPreviewWrapper";
import { DraggableComponent } from "./DraggableComponent";
import {
  resolveColorToBrandkitColorIfNeeded,
  roundToHalfPixel,
  resolveMainImageMediaType,
} from "../../../../helpers/itemsHelper";
import { getMaskClipStyle, isMasked } from "../../../../helpers/maskHelper";
import { ITEM_TYPES, MainImage, TabKeys } from "../../../../types";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { generateStrokeStyles } from "../../../../helpers";
import { useMemo } from "react";
import { Slide } from "pages/media/types/";
import { useTemplateData } from "../../../../hooks/useTemplateData";

export const TemplateMainImage = ({
  previewWidth,
  previewHeight,
  slide,
  mainImage,
  editable = false,
  saveRatio = false,
}: {
  previewWidth: number;
  previewHeight: number;
  slide: Slide;
  mainImage: MainImage | undefined;
  editable?: boolean;
  saveRatio?: boolean;
}) => {
  const { updateMainImage, currentItem } = useTemplateEditorStore((state) => ({
    updateMainImage: state.updateMainImage,
    currentItem: state.templateState.currentItem,
  }));

  const { setActiveTab, activeTab } = useTemplatesStatusStore((state) => ({
    setActiveTab: state.setActiveTab,
    activeTab: state.activeTab,
  }));

  // Prefer the user-replaced asset_id so the preview renders the replacement
  // after reload (mainImage.path isn't persisted server-side; useRefreshAssetPath
  // inside ImageVideoPreview will fetch the asset by id).
  const assetId = useMemo(() => {
    if (!mainImage) return undefined;
    if (mainImage.assetId && mainImage.assetId !== "default") {
      return mainImage.assetId;
    }
    if (
      "image_element" in slide.placeholders &&
      // we need this condition for older projects, before image_element was added to all slide types
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      slide.placeholders.image_element
    ) {
      return slide.placeholders.image_element.asset_id;
    }
    return undefined;
  }, [mainImage, slide.placeholders]);

  // Prefer the user-replaced mediaType so image↔video swaps render correctly;
  // fall back to the slide's original placeholder type.
  const mediaType = useMemo(
    () => resolveMainImageMediaType(mainImage, slide),
    [mainImage, slide]
  );

  const {
    brandKitData: { brandKitPalette },
    placeholderAssets,
  } = useTemplateData();

  if (!mainImage || !assetId || !mainImage.visible) return null;

  const isEditingThis = editable && currentItem?.id === mainImage.id;
  const cropStyle =
    isMasked(mainImage.mask) && !isEditingThis
      ? getMaskClipStyle(mainImage.mask)
      : undefined;

  const videoComponent = (
    <ImageVideoPreview
      type={mediaType === "video" ? ITEM_TYPES.Video : ITEM_TYPES.Image}
      assetId={assetId || undefined}
      path={mainImage.path}
      placeholderAssets={placeholderAssets}
      videoProps={
        editable
          ? {
              controls: currentItem?.id === mainImage.id,
              playsInline: true,
            }
          : undefined
      }
      style={{
        ...generateStrokeStyles(
          {
            ...mainImage,
            stroke: {
              ...mainImage.stroke,
              color: resolveColorToBrandkitColorIfNeeded(
                mainImage.stroke?.color ?? undefined,
                brandKitPalette
              ),
            },
          },
          previewWidth,
          previewHeight
        ),
        ...cropStyle,
      }}
    />
  );

  if (!editable) {
    const strokeWidth = Math.round(
      (mainImage.stroke?.width ?? 0) * previewWidth
    );
    const doubleStrokeWidth = strokeWidth * 2;

    return (
      <div
        key={mainImage.id}
        style={{
          position: "absolute",
          top:
            roundToHalfPixel(mainImage.position.y * previewHeight) -
            strokeWidth,
          left:
            roundToHalfPixel(mainImage.position.x * previewWidth) - strokeWidth,
          width:
            roundToHalfPixel(mainImage.size.width * previewWidth) +
            doubleStrokeWidth,
          height:
            roundToHalfPixel(mainImage.size.height * previewHeight) +
            doubleStrokeWidth,
          zIndex: mainImage.layerIndex,
          transformOrigin: "center",
          transform: mainImage.rotation
            ? `rotate(${mainImage.rotation}deg)`
            : undefined,
        }}
      >
        {videoComponent}
      </div>
    );
  }

  return (
    <DraggableComponent
      key={mainImage.id}
      position={mainImage.position}
      size={mainImage.size}
      rotation={mainImage.rotation}
      layerIndex={mainImage.layerIndex}
      onDragStop={updateMainImage}
      onRotate={(rotation) => updateMainImage({ rotation })}
      saveRatio={saveRatio}
      drag={true}
      resize={true}
      mask={mainImage.mask}
      onMask={(mask) => updateMainImage({ mask })}
      item={{
        type: ITEM_TYPES.Image,
        id: mainImage.id,
      }}
      onFocus={() => {
        if (activeTab !== TabKeys.objects && activeTab !== TabKeys.motion) {
          setActiveTab(TabKeys.objects);
        }
      }}
      previewWidth={previewWidth}
      previewHeight={previewHeight}
      stroke={mainImage.stroke}
    >
      <AnimationPreviewWrapper
        animations={mainImage.animations}
        previewWidth={previewWidth}
        previewHeight={previewHeight}
        itemId={mainImage.id}
        rotation={mainImage.rotation}
      >
        {videoComponent}
      </AnimationPreviewWrapper>
    </DraggableComponent>
  );
};
